import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Form, ListGroup } from 'react-bootstrap';

function norm(s) {
  return (s || '').toLowerCase();
}

/**
 * Локальный фильтр магазинов по подстроке в названии и ID (данные уже загружены в stores).
 */
export default function AccountsStoreFilterCombobox({
  stores,
  value,
  onChange,
  label,
  allStoresLabel,
  placeholder,
  noMatchesText,
}) {
  const blurTimer = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const selected = useMemo(
    () => stores.find((s) => String(s.store_id) === String(value)),
    [stores, value],
  );

  useEffect(() => {
    if (value && selected) {
      setQuery(selected.store_name || String(selected.store_id));
    }
  }, [value, selected]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return stores;
    return stores.filter((s) => {
      const name = norm(s.store_name);
      const id = String(s.store_id ?? '');
      return name.includes(q) || id.includes(query.trim());
    });
  }, [stores, query]);

  const cancelBlur = () => {
    if (blurTimer.current) {
      clearTimeout(blurTimer.current);
      blurTimer.current = null;
    }
  };

  const handleBlur = () => {
    blurTimer.current = setTimeout(() => setOpen(false), 200);
  };

  const handleFocus = () => {
    cancelBlur();
    setOpen(true);
  };

  const handleInputChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    setOpen(true);
    if (value) onChange('');
  };

  const pick = (s) => {
    cancelBlur();
    onChange(String(s.store_id));
    setQuery(s.store_name || String(s.store_id));
    setOpen(false);
  };

  const clearStore = () => {
    cancelBlur();
    onChange('');
    setQuery('');
    setOpen(true);
  };

  const selectAllStores = () => {
    cancelBlur();
    onChange('');
    setQuery('');
    setOpen(false);
  };

  return (
    <Form.Group>
      <Form.Label className="small">{label}</Form.Label>
      <div className="position-relative">
        <div className="d-flex gap-1 align-items-stretch">
          <Form.Control
            type="search"
            autoComplete="off"
            placeholder={placeholder}
            aria-label={placeholder}
            value={query}
            onChange={handleInputChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
          {value ? (
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm px-2 flex-shrink-0"
              onMouseDown={(e) => e.preventDefault()}
              onClick={clearStore}
              aria-label={allStoresLabel}
            >
              ×
            </button>
          ) : null}
        </div>
        {open ? (
          <ListGroup
            className="position-absolute w-100 shadow border rounded mt-1"
            style={{ zIndex: 1050, maxHeight: 240, overflowY: 'auto' }}
          >
            <ListGroup.Item
              action
              className="small py-2"
              active={!value}
              onMouseDown={(e) => e.preventDefault()}
              onClick={selectAllStores}
            >
              {allStoresLabel}
            </ListGroup.Item>
            {filtered.length === 0 && query.trim() !== '' ? (
              <ListGroup.Item className="text-muted small py-2">{noMatchesText}</ListGroup.Item>
            ) : (
              filtered.map((s) => (
                <ListGroup.Item
                  key={s.store_id}
                  action
                  className="small py-2"
                  active={String(value) === String(s.store_id)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pick(s)}
                >
                  <div className="fw-semibold text-truncate">{s.store_name || '—'}</div>
                  <div className="text-muted font-monospace text-truncate">{s.store_id}</div>
                </ListGroup.Item>
              ))
            )}
          </ListGroup>
        ) : null}
      </div>
    </Form.Group>
  );
}
